import {
  type Dispatch,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

type UseEntityDailyHistoryNameColumnResizeArgs = {
  clampNameColumnWidth: (widthPx: number) => number;
  nameColumnDefaultWidthPx: number;
  nameColumnHeaderCellRef: RefObject<HTMLTableCellElement | null>;
};

type UseEntityDailyHistoryNameColumnResizeResult = {
  handleNameColumnResizeKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>) => void;
  handleNameColumnResizeStart: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  nameColumnWidth: string;
  resizedNameColumnWidthPx: number | null;
  setResizedNameColumnWidthPx: Dispatch<SetStateAction<number | null>>;
};

export const useEntityDailyHistoryNameColumnResize = ({
  clampNameColumnWidth,
  nameColumnDefaultWidthPx,
  nameColumnHeaderCellRef,
}: UseEntityDailyHistoryNameColumnResizeArgs): UseEntityDailyHistoryNameColumnResizeResult => {
  const [resizedNameColumnWidthPx, setResizedNameColumnWidthPx] = useState<number | null>(null);
  const activeNameColumnResizeCleanupRef = useRef<(() => void) | null>(null);

  const resolveCurrentNameColumnWidthPx = useCallback((): number => {
    if (resizedNameColumnWidthPx === null) {
      return clampNameColumnWidth(nameColumnDefaultWidthPx);
    }

    return clampNameColumnWidth(resizedNameColumnWidthPx);
  }, [clampNameColumnWidth, nameColumnDefaultWidthPx, resizedNameColumnWidthPx]);

  const nameColumnWidth =
    resizedNameColumnWidthPx === null
      ? `${String(clampNameColumnWidth(nameColumnDefaultWidthPx))}px`
      : `${String(clampNameColumnWidth(resizedNameColumnWidthPx))}px`;

  const clearNameColumnResizeSession = useCallback((): void => {
    const cleanup = activeNameColumnResizeCleanupRef.current;
    if (cleanup === null) {
      return;
    }

    activeNameColumnResizeCleanupRef.current = null;
    cleanup();
  }, []);

  useEffect(() => clearNameColumnResizeSession, [clearNameColumnResizeSession]);

  const handleNameColumnResizeStart = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    const headerCellElement = nameColumnHeaderCellRef.current;
    if (!headerCellElement) {
      return;
    }

    event.preventDefault();
    clearNameColumnResizeSession();

    const startX = event.clientX;
    const startWidth = headerCellElement.getBoundingClientRect().width;
    const pointerId = event.pointerId;
    const targetElement = event.currentTarget;

    targetElement.setPointerCapture(pointerId);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handlePointerMove = (moveEvent: PointerEvent): void => {
      const nextWidth = startWidth + (moveEvent.clientX - startX);
      setResizedNameColumnWidthPx(clampNameColumnWidth(nextWidth));
    };

    const cleanupResizeSession = (): void => {
      if (targetElement.hasPointerCapture(pointerId)) {
        targetElement.releasePointerCapture(pointerId);
      }

      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    const handlePointerUp = (): void => {
      clearNameColumnResizeSession();
    };

    activeNameColumnResizeCleanupRef.current = cleanupResizeSession;
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
  };

  const handleNameColumnResizeKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
    const currentWidth = resolveCurrentNameColumnWidthPx();
    const resizeStepPx = event.shiftKey ? 24 : 12;

    if (event.key === 'Home') {
      event.preventDefault();
      setResizedNameColumnWidthPx(clampNameColumnWidth(0));
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setResizedNameColumnWidthPx(clampNameColumnWidth(Number.POSITIVE_INFINITY));
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();
    const nextWidth =
      currentWidth + (event.key === 'ArrowRight' ? resizeStepPx : resizeStepPx * -1);
    setResizedNameColumnWidthPx(clampNameColumnWidth(nextWidth));
  };

  return {
    handleNameColumnResizeKeyDown,
    handleNameColumnResizeStart,
    nameColumnWidth,
    resizedNameColumnWidthPx,
    setResizedNameColumnWidthPx,
  };
};
